import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Mail, CheckCircle, XCircle, Clock, Video, RefreshCw, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { ALL_MEETING_STATUSES } from "@/lib/meetingStatus";

interface MeetingDiagnostics {
  id: string;
  status: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  zoom_join_url: string | null;
  zoom_meeting_id: string | null;
  last_invite_sent_at: string | null;
  last_invite_status: string | null;
  calendar_uid: string | null;
  calendar_sequence: number | null;
  created_at: string;
  mentor_id: string;
  entrepreneur_id: string;
  mentor_name: string;
  mentor_avatar: string | null;
  mentor_email_present: boolean;
  entrepreneur_name: string;
  entrepreneur_avatar: string | null;
  entrepreneur_email_present: boolean;
  latest_error_log: {
    created_at: string;
    error_message: string;
  } | null;
}

export function MentorshipMeetingsDiagnostics() {
  const queryClient = useQueryClient();
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  // Fetch scheduled meetings with diagnostics info
  const { data: meetings, isLoading, refetch } = useQuery({
    queryKey: ["admin_mentorship_meetings_diagnostics"],
    queryFn: async () => {
      // Fetch meetings that are scheduled or recently completed
      const { data: meetingsData, error: meetingsError } = await supabase
        .from("mentorship_meetings")
        .select(`
          id,
          status,
          scheduled_start_at,
          scheduled_end_at,
          zoom_join_url,
          zoom_meeting_id,
          last_invite_sent_at,
          last_invite_status,
          calendar_uid,
          calendar_sequence,
          created_at,
          mentor_id,
          entrepreneur_id
        `)
        // Diagnostics must show every status, including the Zoom/invite
        // pipeline states and 'failed' — those are the ones worth debugging.
        .in("status", ALL_MEETING_STATUSES)
        .order("created_at", { ascending: false })
        .limit(50);

      if (meetingsError) throw meetingsError;
      if (!meetingsData || meetingsData.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(meetingsData.flatMap((m) => [m.mentor_id, m.entrepreneur_id]))];

      // Fetch profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      // Fetch profile_private to check if emails exist
      const { data: privateProfiles } = await supabase
        .from("profile_private")
        .select("user_id, email")
        .in("user_id", userIds);

      // Fetch latest error logs for these meetings
      const meetingIds = meetingsData.map((m) => m.id);
      const { data: errorLogs } = await supabase
        .from("error_logs")
        .select("metadata, created_at, error_message")
        .eq("component", "send-calendar-invite")
        .order("created_at", { ascending: false })
        .limit(100);

      // Map meetings with additional info
      return meetingsData.map((meeting) => {
        const mentorProfile = profiles?.find((p) => p.id === meeting.mentor_id);
        const entrepreneurProfile = profiles?.find((p) => p.id === meeting.entrepreneur_id);
        const mentorPrivate = privateProfiles?.find((p) => p.user_id === meeting.mentor_id);
        const entrepreneurPrivate = privateProfiles?.find((p) => p.user_id === meeting.entrepreneur_id);

        // Find latest error log for this meeting
        const meetingErrorLogs = errorLogs?.filter((log) => {
          const metadata = log.metadata as { meeting_id?: string } | null;
          return metadata?.meeting_id === meeting.id;
        });
        const latestError = meetingErrorLogs && meetingErrorLogs.length > 0 ? meetingErrorLogs[0] : null;

        return {
          id: meeting.id,
          status: meeting.status,
          scheduled_start_at: meeting.scheduled_start_at,
          scheduled_end_at: meeting.scheduled_end_at,
          zoom_join_url: meeting.zoom_join_url,
          zoom_meeting_id: meeting.zoom_meeting_id,
          last_invite_sent_at: meeting.last_invite_sent_at,
          last_invite_status: meeting.last_invite_status,
          calendar_uid: meeting.calendar_uid,
          calendar_sequence: meeting.calendar_sequence,
          created_at: meeting.created_at,
          mentor_id: meeting.mentor_id,
          entrepreneur_id: meeting.entrepreneur_id,
          mentor_name: mentorProfile?.full_name || "Unknown",
          mentor_avatar: mentorProfile?.avatar_url || null,
          mentor_email_present: !!mentorPrivate?.email,
          entrepreneur_name: entrepreneurProfile?.full_name || "Unknown",
          entrepreneur_avatar: entrepreneurProfile?.avatar_url || null,
          entrepreneur_email_present: !!entrepreneurPrivate?.email,
          latest_error_log: latestError ? {
            created_at: latestError.created_at,
            error_message: latestError.error_message,
          } : null,
        } as MeetingDiagnostics;
      });
    },
  });

  // Send ICS invite mutation
  const sendInvite = useMutation({
    mutationFn: async (meetingId: string) => {
      setSendingInvite(meetingId);
      const { data, error } = await supabase.functions.invoke("send-calendar-invite", {
        body: { meetingId, action: "create" },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data, meetingId) => {
      setSendingInvite(null);
      queryClient.invalidateQueries({ queryKey: ["admin_mentorship_meetings_diagnostics"] });
      
      if (data?.skipped) {
        toast.info(`Invite skipped: ${data.skipped}`);
      } else if (data?.success) {
        toast.success("Calendar invite sent successfully!");
      } else {
        toast.error("Invite sending returned unexpected result");
      }
    },
    onError: (error: Error) => {
      setSendingInvite(null);
      toast.error("Failed to send invite: " + error.message);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "scheduled":
        return <Badge className="bg-green-500">Scheduled</Badge>;
      case "proposed":
        return <Badge variant="secondary">Proposed</Badge>;
      case "completed":
        return <Badge className="bg-blue-500">Completed</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInviteStatusBadge = (status: string | null) => {
    if (!status) return <Badge variant="outline">Never Sent</Badge>;
    switch (status) {
      case "sent":
        return <Badge className="bg-green-500">Sent</Badge>;
      case "partial_failure":
        return <Badge className="bg-yellow-500">Partial</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {meetings?.length || 0} meeting(s) found
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {(!meetings || meetings.length === 0) ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No mentorship meetings found</p>
          </CardContent>
        </Card>
      ) : (
        meetings.map((meeting) => (
          <Card key={meeting.id} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={meeting.mentor_avatar || undefined} />
                      <AvatarFallback>{meeting.mentor_name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="mx-2 text-muted-foreground">↔</span>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={meeting.entrepreneur_avatar || undefined} />
                      <AvatarFallback>{meeting.entrepreneur_name.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </div>
                  <div>
                    <CardTitle className="text-sm font-medium">
                      {meeting.mentor_name} & {meeting.entrepreneur_name}
                    </CardTitle>
                  </div>
                </div>
                {getStatusBadge(meeting.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Diagnostics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Meeting ID</p>
                  <p className="font-mono text-xs truncate">{meeting.id}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Scheduled At</p>
                  <p className="text-xs">
                    {meeting.scheduled_start_at 
                      ? format(new Date(meeting.scheduled_start_at), "MMM d, yyyy h:mm a")
                      : <span className="text-muted-foreground">Not set</span>
                    }
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Zoom URL</p>
                  <div className="flex items-center gap-1">
                    {meeting.zoom_join_url ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                    <span className="text-xs">{meeting.zoom_join_url ? "Present" : "Missing"}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Mentor Email</p>
                  <div className="flex items-center gap-1">
                    {meeting.mentor_email_present ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                    <span className="text-xs">{meeting.mentor_email_present ? "Present" : "Missing"}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Entrepreneur Email</p>
                  <div className="flex items-center gap-1">
                    {meeting.entrepreneur_email_present ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                    <span className="text-xs">{meeting.entrepreneur_email_present ? "Present" : "Missing"}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Invite Status</p>
                  {getInviteStatusBadge(meeting.last_invite_status)}
                </div>
              </div>

              {/* Last Invite Sent */}
              {meeting.last_invite_sent_at && (
                <div className="text-xs text-muted-foreground">
                  Last invite sent: {format(new Date(meeting.last_invite_sent_at), "MMM d, yyyy h:mm a")}
                </div>
              )}

              {/* Error Log */}
              {meeting.latest_error_log && (
                <div className="flex items-start gap-2 p-2 bg-destructive/10 rounded text-xs">
                  <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Error ({format(new Date(meeting.latest_error_log.created_at), "MMM d h:mm a")})</p>
                    <p className="text-muted-foreground truncate max-w-md">{meeting.latest_error_log.error_message}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => sendInvite.mutate(meeting.id)}
                  disabled={sendingInvite === meeting.id}
                >
                  {sendingInvite === meeting.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send ICS Invite Now
                </Button>
                {meeting.zoom_join_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => window.open(meeting.zoom_join_url!, "_blank")}
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Open Zoom
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}